import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Autocomplete,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { fetchHotelById } from '../../store/slices/hotelSlice';
import hotelService from '../../services/api/hotelService';
import locationService from '../../services/api/locationService';

const HotelFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentHotel, loading } = useSelector((state) => state.hotels);
  const isEdit = !!id;
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [countries, setCountries] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState('');
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [amenityInput, setAmenityInput] = useState('');

  const commonAmenities = [
    'WiFi', 'Parking', 'Pool', 'Gym', 'Spa', 'Restaurant', 'Room Service',
    'Laundry', 'Air Conditioning', 'TV', 'Minibar', 'Safe', 'Balcony',
    'Prayer Room', 'Qibla Direction', 'Haram View'
  ];

  const { control, handleSubmit, formState: { errors }, reset, watch } = useForm({
    defaultValues: {
      name: '',
      nameAr: '',
      cityId: '',
      areaId: '',
      address: '',
      addressAr: '',
      starRating: '',
      distanceFromHaram: '',
      latitude: '',
      longitude: '',
      description: '',
      descriptionAr: '',
      contactPhone: '',
      contactEmail: '',
      website: '',
      checkInTime: '',
      checkOutTime: '',
      isActive: true,
    },
  });

  const watchedCityId = watch('cityId');

  useEffect(() => {
    fetchCountries();
    if (isEdit && id) {
      dispatch(fetchHotelById(id));
    }
  }, [id, isEdit, dispatch]);

  useEffect(() => {
    if (selectedCountryId) {
      fetchCities(selectedCountryId);
    } else {
      setCities([]);
    }
  }, [selectedCountryId]);

  useEffect(() => {
    if (watchedCityId) {
      fetchAreas(watchedCityId);
    }
  }, [watchedCityId]);

  useEffect(() => {
    if (currentHotel && isEdit) {
      reset({
        name: currentHotel.name || '',
        nameAr: currentHotel.nameAr || '',
        cityId: currentHotel.cityId || '',
        areaId: currentHotel.areaId || '',
        address: currentHotel.address || '',
        addressAr: currentHotel.addressAr || '',
        starRating: currentHotel.starRating || '',
        distanceFromHaram: currentHotel.distanceFromHaram || '',
        latitude: currentHotel.latitude || '',
        longitude: currentHotel.longitude || '',
        description: currentHotel.description || '',
        descriptionAr: currentHotel.descriptionAr || '',
        contactPhone: currentHotel.contactPhone || '',
        contactEmail: currentHotel.contactEmail || '',
        website: currentHotel.website || '',
        checkInTime: currentHotel.checkInTime || '',
        checkOutTime: currentHotel.checkOutTime || '',
        isActive: currentHotel.isActive ?? true,
      });
      setAmenities(currentHotel.amenities || []);
      if (currentHotel.cityId) {
        fetchAreas(currentHotel.cityId);
        locationService.getAllCities().then((all) => {
          const city = all.find((c) => c.id === currentHotel.cityId);
          if (city?.countryId) {
            setSelectedCountryId(city.countryId);
          }
        });
      }
    }
  }, [currentHotel, isEdit, reset]);

  const fetchCountries = async () => {
    try {
      const data = await locationService.getAllCountries();
      setCountries(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchCities = async (countryId) => {
    try {
      const data = await locationService.getAllCities(countryId);
      setCities(data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchAreas = async (cityId) => {
    try {
      const data = await locationService.getAreasByCity(cityId);
      setAreas(data);
    } catch (error) {
      console.error('Error fetching areas:', error);
    }
  };

  const handleAddAmenity = () => {
    if (amenityInput && !amenities.includes(amenityInput)) {
      setAmenities([...amenities, amenityInput]);
      setAmenityInput('');
    }
  };

  const handleRemoveAmenity = (amenity) => {
    setAmenities(amenities.filter(a => a !== amenity));
  };

  const onSubmit = async (data) => {
    try {
      setSubmitLoading(true);
      setError(null);
      
      const submitData = {
        ...data,
        starRating: data.starRating ? parseInt(data.starRating) : null,
        distanceFromHaram: data.distanceFromHaram ? parseFloat(data.distanceFromHaram) : null,
        latitude: data.latitude ? parseFloat(data.latitude) : null,
        longitude: data.longitude ? parseFloat(data.longitude) : null,
        checkInTime: data.checkInTime || null,
        checkOutTime: data.checkOutTime || null,
        areaId: data.areaId || null,
        amenities: amenities,
      };
      
      if (isEdit) {
        await hotelService.updateHotel(id, submitData);
      } else {
        await hotelService.createHotel(submitData);
      }
      
      navigate('/hotels');
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving hotel');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {isEdit ? 'Edit Hotel' : 'Create New Hotel'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mt: 2 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Hotel name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Hotel Name (English)"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="nameAr"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Hotel Name (Arabic)"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  value={selectedCountryId}
                  label="Country"
                  onChange={(e) => {
                    setSelectedCountryId(e.target.value);
                    reset({ ...watch(), cityId: '', areaId: '' });
                  }}
                >
                  {countries.map((country) => (
                    <MenuItem key={country.id} value={country.id}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="cityId"
                control={control}
                rules={{ required: 'City is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.cityId} disabled={!selectedCountryId}>
                    <InputLabel>City</InputLabel>
                    <Select {...field} label="City">
                      {cities.map((city) => (
                        <MenuItem key={city.id} value={city.id}>
                          {city.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.cityId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {errors.cityId.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="areaId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Area (Optional)</InputLabel>
                    <Select {...field} label="Area (Optional)" disabled={!watchedCityId}>
                      <MenuItem value="">None</MenuItem>
                      {areas.map((area) => (
                        <MenuItem key={area.id} value={area.id}>
                          {area.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="address"
                control={control}
                rules={{ required: 'Address is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Address"
                    error={!!errors.address}
                    helperText={errors.address?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="addressAr"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Address (Arabic)"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="starRating"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Star Rating</InputLabel>
                    <Select {...field} label="Star Rating">
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="1">1 Star</MenuItem>
                      <MenuItem value="2">2 Stars</MenuItem>
                      <MenuItem value="3">3 Stars</MenuItem>
                      <MenuItem value="4">4 Stars</MenuItem>
                      <MenuItem value="5">5 Stars</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="distanceFromHaram"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Distance from Haram (km)"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      {...field}
                      value={field.value ? 'true' : 'false'}
                      onChange={(e) => field.onChange(e.target.value === 'true')}
                      label="Status"
                    >
                      <MenuItem value="true">Active</MenuItem>
                      <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="latitude"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Latitude"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="longitude"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Longitude"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Description"
                    multiline
                    rows={4}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="contactPhone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Contact Phone"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="contactEmail"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="email"
                    label="Contact Email"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="website"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Website"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="checkInTime"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="time"
                    label="Check-in Time"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="checkOutTime"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="time"
                    label="Check-out Time"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Amenities
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {amenities.map((amenity) => (
                  <Chip
                    key={amenity}
                    label={amenity}
                    onDelete={() => handleRemoveAmenity(amenity)}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Autocomplete
                  freeSolo
                  options={commonAmenities}
                  value={amenityInput}
                  onChange={(e, newValue) => setAmenityInput(newValue || '')}
                  onInputChange={(e, newInputValue) => setAmenityInput(newInputValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add Amenity"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddAmenity();
                        }
                      }}
                    />
                  )}
                  sx={{ flexGrow: 1 }}
                />
                <Button onClick={handleAddAmenity} variant="outlined">
                  Add
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitLoading}
                >
                  {submitLoading ? <CircularProgress size={24} /> : isEdit ? 'Update Hotel' : 'Create Hotel'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/hotels')}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default HotelFormPage;

